/**
 * Корневой компонент: авторизация, тема, маршруты.
 */

import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ScreenBoundary } from './components/ScreenBoundary.js';
import { UpdateNotice } from './components/UpdateNotice.js';
import { useBootstrap, useLogout, useMe } from './api/queries.js';
import { Shell } from './layout/Shell.js';
import { Login } from './screens/Login.js';
import { Orders } from './screens/Orders.js';
import { Doctors } from './screens/Doctors.js';
import { Clinics } from './screens/Clinics.js';
import { Constructions } from './screens/Constructions.js';
import { Documents } from './screens/Documents.js';
import { Employees } from './screens/Employees.js';
import { Finance } from './screens/Finance.js';
import { MySalary } from './screens/MySalary.js';
import { Settings } from './screens/Settings.js';
import { ErrorState, Spinner } from './components/ui.js';
import { applyColors, applyTheme } from './theme.js';

export function App() {
  const location = useLocation();
  const me = useMe();
  const authenticated = me.isSuccess && Boolean(me.data?.user);
  const bootstrap = useBootstrap(authenticated);
  const logout = useLogout();

  /*
   * Тема ЛИЧНАЯ: у каждого своя, выбранная в «Настройки → Оформление».
   * Кто не выбирал — у того тема лаборатории, как было до личных тем.
   */
  const themeName = me.data?.user?.themeName ?? bootstrap.data?.settings?.themeName;
  const themePair = me.data?.user?.themePair;
  useEffect(() => {
    if (themeName) applyTheme(themeName, themePair);
  }, [themeName, themePair?.accent, themePair?.accentDark]);

  /*
   * Цвета статусов и «Финансов» — так же общие и так же со справочниками.
   * Пока их нет (старый сервер, ещё не загрузилось), стоят стандартные.
   */
  const colors = bootstrap.data?.settings?.colors;
  useEffect(() => {
    if (colors) applyColors(colors);
  }, [colors]);

  if (me.isLoading) return <Spinner label="Проверка сессии…" />;
  if (!authenticated) return <Login />;

  const user = me.data!.user;

  return (
    <Shell user={user} onLogout={() => logout.mutate()}>
      {/*
        Неудачная загрузка справочников ПОКАЗЫВАЕТСЯ, а не крутится вечно.

        Раньше здесь стояла одна крутилка на все случаи. Пропала связь — и
        программа до самой перезагрузки писала «Загрузка справочников…», хотя
        давно перестала чего-либо ждать. На телефоне, где сеть отваливается на
        ходу, это выглядело как намертво зависшая программа.

        Приостановка (`paused`) разбирается отдельно от отказа: браузер сообщил,
        что связи нет, и запрос даже не уходил. Ошибки при этом никакой нет, и
        по одному `isError` такой случай не отловить.
      */}
      {bootstrap.isError || bootstrap.fetchStatus === 'paused' ? (
        <ErrorState
          error={
            bootstrap.error instanceof Error
              ? bootstrap.error
              : new Error('Нет связи с сервером — справочники не загрузились')
          }
          onRetry={() => void bootstrap.refetch()}
        />
      ) : bootstrap.isLoading ? (
        <Spinner label="Загрузка справочников…" />
      ) : (
        /*
          Падение раздела не должно гасить всю программу: граница ловит его
          и показывает, что случилось. Ключ — адрес: перешли в другой раздел,
          и ошибка не тянется за нами.
        */
        <ScreenBoundary ключ={location.pathname}>
          <Routes>
            <Route path="/" element={<Navigate to="/orders" replace />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/doctors" element={<Doctors />} />
            <Route path="/clinics" element={<Clinics />} />
            <Route path="/constructions" element={<Constructions />} />
            <Route path="/documents/*" element={<Documents />} />
            <Route path="/employees" element={<Employees />} />
            <Route path="/finance" element={<Finance />} />
            <Route path="/my-salary" element={<MySalary />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/orders" replace />} />
          </Routes>
        </ScreenBoundary>
      )}
      {/*
        Полоса «вышло обновление» — поверх любого раздела: программу держат
        открытой неделями, а выкаты идут по нескольку раз в день.
      */}
      <UpdateNotice />
    </Shell>
  );
}
